import { Component, Inject, HostListener } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { BasePreviewComponent } from '../base-preview.component';
import { UNSUPPORTED_FILE_PREVIEW_MESSAGE } from 'src/app/constants/app.constants';

@Component({
    selector: 'app-image-preview',
    templateUrl: './image-preview.component.html',
    styleUrls: ['./image-preview.component.scss'],
    standalone: false
})
export class ImagePreviewComponent extends BasePreviewComponent {
  // Image State
  isLoading = true;
  zoomLevel = 100; // percentage
  rotation = 0; // degrees
  /** Details panel toggled by the header info button. */
  showInfoPanel = false;

  /**
   * Optional metadata rows passed by the host via the dialog data
   * (`data.meta = [{ icon?, label, value }]`). Generic + reusable — any
   * gallery/host can supply its own rows; the info icon only shows when
   * rows are present.
   */
  get meta(): Array<{ icon?: string; label: string; value: string }> {
    return Array.isArray(this.data?.meta) ? this.data.meta : [];
  }

  toggleInfo(): void {
    this.showInfoPanel = !this.showInfoPanel;
  }
  isDragging = false;
  startX = 0;
  startY = 0;
  scrollLeft = 0;
  scrollTop = 0;

  constructor(
    public override dialogRef: MatDialogRef<ImagePreviewComponent>,
    @Inject(MAT_DIALOG_DATA) public override data: any,
    private toastr: ToastrService
  ) {
    super(dialogRef, data);
  }

  // ========================================
  // Image Event Handlers
  // ========================================

  onImageLoad(): void {
    this.isLoading = false;
  }

  onImageError(): void {
    this.isLoading = false;
    // Browser can't render this image (e.g. .tiff and other non-web formats) —
    // show the generic message and close.
    this.toastr.error(UNSUPPORTED_FILE_PREVIEW_MESSAGE);
    this.dialogRef.close();
  }

  // ========================================
  // Zoom Controls
  // ========================================

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 25, 200);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 25, 50);
  }

  resetZoom(): void {
    this.zoomLevel = 100;
    this.rotation = 0;
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }
  }

  // ========================================
  // Rotation Controls
  // ========================================

  rotateLeft(): void {
    this.rotation = (this.rotation - 90) % 360;
  }

  rotateRight(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  // ========================================
  // Pan/Drag Controls
  // ========================================

  onMouseDown(event: MouseEvent): void {
    if (this.zoomLevel > 100) {
      this.isDragging = true;
      const container = event.currentTarget as HTMLElement;
      this.startX = event.pageX - container.offsetLeft;
      this.startY = event.pageY - container.offsetTop;
      this.scrollLeft = container.scrollLeft;
      this.scrollTop = container.scrollTop;
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    event.preventDefault();
    const container = document.querySelector('.image-container') as HTMLElement;
    if (container) {
      const x = event.pageX - container.offsetLeft;
      const y = event.pageY - container.offsetTop;
      const walkX = (x - this.startX) * 2;
      const walkY = (y - this.startY) * 2;
      container.scrollLeft = this.scrollLeft - walkX;
      container.scrollTop = this.scrollTop - walkY;
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  // ========================================
  // Image Transform Style
  // ========================================

  getImageStyle(): any {
    return {
      'transform': `scale(${this.zoomLevel / 100}) rotate(${this.rotation}deg)`,
      'cursor': this.zoomLevel > 100 ? (this.isDragging ? 'grabbing' : 'grab') : 'default'
    };
  }

  // ========================================
  // Download Override from Base
  // ========================================

  override async downloadMedia(event: Event) {
    event.preventDefault();
    const url = this.getMediaUrl();

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${this.model?.original_name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: direct download
      window.location.href = url;
    }
  }

}
